import { Divider, PopoverTitle, Skeleton } from "@coval/ui"
import { Bookmark, User, Users } from "lucide-react"

import { SavedViewItem } from "./SavedViewItem"
import type { SavedView } from "./types"

/**
 * Two-section list of saved views ("Organization Views" + "My Views"), with
 * loading/empty states. Generic over the config shape.
 *
 * Parent components are responsible for splitting views into orgViews/myViews
 * (by reading `view.config.metadata.isShared` and comparing `view.userId` to
 * the current user) — this component is presentational only.
 */
interface ViewsListProps<TConfig> {
  orgViews: SavedView<TConfig>[]
  myViews: SavedView<TConfig>[]
  loadedViewId: string | null
  editingViewId: string | null
  confirmDeleteId: string | null
  isLoadingViews: boolean
  onLoadView: (view: SavedView<TConfig>) => void
  onStartEdit: (viewId: string, name: string) => void
  onCancelEdit: () => void
  onConfirmEdit: (viewId: string, name: string) => void | Promise<void>
  onStartDelete: (viewId: string) => void
  onCancelDelete: () => void
  onConfirmDelete: (viewId: string) => void
  onUpdateView?: (viewId: string) => void
  canUpdateLoadedView?: boolean
  isUpdating?: boolean
  /** Hides rename and delete for views the current user cannot change. Defaults to allowing every view. */
  canManageView?: (view: SavedView<TConfig>) => boolean
}

export function ViewsList<TConfig>({
  orgViews,
  myViews,
  loadedViewId,
  editingViewId,
  confirmDeleteId,
  isLoadingViews,
  onLoadView,
  onStartEdit,
  onCancelEdit,
  onConfirmEdit,
  onStartDelete,
  onCancelDelete,
  onConfirmDelete,
  onUpdateView,
  canUpdateLoadedView,
  isUpdating,
  canManageView,
}: ViewsListProps<TConfig>) {
  return (
    <>
      <Divider />
      <div className="max-h-[400px] overflow-y-auto">
        {isLoadingViews ? (
          <div className="space-y-3 p-4">
            <Skeleton className="h-10 w-full rounded" />
            <Skeleton className="h-10 w-full rounded" />
            <Skeleton className="h-10 w-3/4 rounded" />
          </div>
        ) : (
          <>
            {orgViews.length > 0 && (
              <div>
                <PopoverTitle className="flex items-center gap-2 pl-5 pt-4">
                  <Users className="size-3.5" />
                  Organization Views
                </PopoverTitle>
                <div>
                  {orgViews.map((view) => (
                    <SavedViewItem
                      key={view.ulid}
                      view={view}
                      loadedViewId={loadedViewId}
                      editingViewId={editingViewId}
                      confirmDeleteId={confirmDeleteId}
                      onLoadView={onLoadView}
                      onStartEdit={onStartEdit}
                      onCancelEdit={onCancelEdit}
                      onConfirmEdit={onConfirmEdit}
                      onStartDelete={onStartDelete}
                      onCancelDelete={onCancelDelete}
                      onConfirmDelete={onConfirmDelete}
                      onUpdateView={onUpdateView}
                      canUpdateLoadedView={canUpdateLoadedView}
                      canManage={canManageView?.(view) ?? true}
                      isUpdating={isUpdating}
                    />
                  ))}
                </div>
              </div>
            )}

            {myViews.length > 0 && (
              <>
                {orgViews.length > 0 && <Divider />}
                <div>
                  <PopoverTitle className="flex items-center gap-2  pl-5 pt-4">
                    <User className="size-3.5" />
                    My Views
                  </PopoverTitle>
                  <div>
                    {myViews.map((view) => (
                      <SavedViewItem
                        key={view.ulid}
                        view={view}
                        loadedViewId={loadedViewId}
                        editingViewId={editingViewId}
                        confirmDeleteId={confirmDeleteId}
                        onLoadView={onLoadView}
                        onStartEdit={onStartEdit}
                        onCancelEdit={onCancelEdit}
                        onConfirmEdit={onConfirmEdit}
                        onStartDelete={onStartDelete}
                        onCancelDelete={onCancelDelete}
                        onConfirmDelete={onConfirmDelete}
                        onUpdateView={onUpdateView}
                        canUpdateLoadedView={canUpdateLoadedView}
                        canManage={canManageView?.(view) ?? true}
                        isUpdating={isUpdating}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}

            {orgViews.length === 0 && myViews.length === 0 && (
              <div className="px-4 py-8 text-center">
                <Bookmark className="size-8 text-coval-text-muted mx-auto mb-3" />
                <div className="text-sm font-medium text-coval-text-muted mb-1">
                  No saved views yet
                </div>
                <div className="text-xs text-coval-text-muted">
                  Configure your filters and save your first view
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
